import { useMemo } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Surface } from '@heroui/react';

export type FeatureConfigItem = {
  code: string;
  title: string;
  description: string;
  sort: number;
};

export type PlanFeatureCompareProduct = {
  id: string;
  name?: string | null;
  featureConfig?: FeatureConfigItem[] | null;
};

type CompareRow = {
  code: string;
  title: string;
  level: number;
};

function normalizeFeatureConfig(
  value: PlanFeatureCompareProduct['featureConfig']
): FeatureConfigItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is FeatureConfigItem =>
      Boolean(item && typeof item.code === 'string' && typeof item.title === 'string')
  );
}

/** 以基准产品的 feature_config 生成对比行（大项 + 小项，按 sort / code） */
function buildCompareRows(baseline: FeatureConfigItem[]): CompareRow[] {
  const majors = baseline
    .filter((item) => item.code.split('.').length === 1)
    .slice()
    .sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code));

  const minors = baseline
    .filter((item) => item.code.split('.').length === 2)
    .slice()
    .sort((a, b) => a.sort - b.sort || a.code.localeCompare(b.code));

  const rows: CompareRow[] = [];
  for (const major of majors) {
    rows.push({ code: major.code, title: major.title, level: 1 });
    for (const minor of minors) {
      if (!minor.code.startsWith(`${major.code}.`)) continue;
      rows.push({ code: minor.code, title: minor.title, level: 2 });
    }
  }
  return rows;
}

function descriptionByCode(
  items: FeatureConfigItem[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    map.set(item.code, item.description ?? '');
  }
  return map;
}

function renderCellValue(description: string | undefined, isMajor: boolean) {
  if (isMajor) return null;
  const text = (description ?? '').trim();
  if (!text) return null;
  if (text === '支持') {
    return <IconCheck className="mx-auto size-5 text-success" stroke={2.5} />;
  }
  return text;
}

interface PlanFeatureCompareProps {
  products: PlanFeatureCompareProduct[];
}

export function PlanFeatureCompare({ products }: PlanFeatureCompareProps) {
  const { columns, rows, valueMaps } = useMemo(() => {
    const columns = products.map((p) => ({
      id: String(p.id),
      name: p.name?.trim() || '未命名产品',
    }));

    const baselineConfig = normalizeFeatureConfig(products[0]?.featureConfig);
    const rows = buildCompareRows(baselineConfig);
    const valueMaps = products.map((p) => descriptionByCode(normalizeFeatureConfig(p.featureConfig)));

    return { columns, rows, valueMaps };
  }, [products]);

  if (products.length === 0) {
    return (
      <section className="mt-14 flex flex-col items-center gap-6">
        <h3 className="text-2xl font-bold text-foreground">功能对比</h3>
        <p className="text-sm text-default-500">暂无套餐可对比</p>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="mt-14 flex flex-col items-center gap-6">
        <h3 className="text-2xl font-bold text-foreground">功能对比</h3>
        <p className="text-sm text-default-500">当前套餐尚未配置功能对照</p>
      </section>
    );
  }

  const colTemplate = `minmax(10rem,1.4fr) repeat(${columns.length}, minmax(7rem,1fr))`;

  return (
    <section className="mt-14 flex flex-col items-center gap-6">
      <h3 className="text-2xl font-bold text-foreground">功能对比</h3>

      <Surface className="w-full overflow-hidden rounded-2xl bg-content1">
        <div className="overflow-x-auto">
          <div className="min-w-[40rem]" role="table" aria-label="功能对比">
            <div
              className="grid border-b border-default-200 px-4 py-4 text-sm font-semibold text-foreground sm:px-6"
              style={{ gridTemplateColumns: colTemplate }}
              role="row"
            >
              <div role="columnheader" className="pr-4 text-left">
                对比项目
              </div>
              {columns.map((col) => (
                <div key={col.id} role="columnheader" className="px-2 text-center">
                  {col.name}
                </div>
              ))}
            </div>

            {rows.map((row) => {
              const isMajor = row.level === 1;
              return (
                <div
                  key={row.code}
                  className="grid items-center border-b border-default-100 px-4 py-3.5 last:border-b-0 sm:px-6"
                  style={{ gridTemplateColumns: colTemplate }}
                  role="row"
                >
                  <div
                    role="cell"
                    className={
                      isMajor
                        ? 'pr-4 text-left text-sm font-semibold text-foreground'
                        : 'pr-4 text-left text-sm text-foreground'
                    }
                  >
                    {row.title}
                  </div>
                  {valueMaps.map((map, index) => (
                    <div
                      key={`${row.code}-${columns[index]?.id ?? index}`}
                      role="cell"
                      className="px-2 text-center text-sm text-foreground"
                    >
                      {renderCellValue(map.get(row.code), isMajor)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </Surface>
    </section>
  );
}
